import { computed, ref, type ComputedRef, type Ref } from 'vue'
import type { WikiPage } from '../stores/wiki_pages'
import { categoryLabel } from './categoryLabel'
import { GENERAL_BUCKET, chipBucket } from './categoryBucket'

// Catalog-filter state for HomeLanding's chip strip:
//   - `chips`: distinct buckets present in the catalog with their
//     display labels and per-bucket counts. "General" sorts to the
//     front so it lands where the sidebar puts it; the rest are
//     alphabetical.
//   - `activeFilter`: the currently-selected chip's raw key, or null
//     when no filter is active. Single-select toggle — clicking the
//     active chip clears.
//   - `filterPages(list)`: applies the active filter to a page list,
//     returning the same list unchanged when no filter is active.
//   - `activeFilterLabel`: the display name of the active bucket, or
//     null. Hosts use this for "Recently updated in <X>" headings.
//
// The filter lives on the host (single-select toggle behaviour, no
// URL persistence today) so the surrounding view can keep using `/`
// as its route. Pages are passed in as a ref so the composable
// stays reactive when ingest broadcasts add/remove rows.

export interface CategoryChip {
  raw: string
  display: string
  count: number
}

export interface CategoryFilter {
  chips: ComputedRef<CategoryChip[]>
  activeFilter: Ref<string | null>
  toggleFilter: (raw: string) => void
  clearFilter: () => void
  filterPages: (list: readonly WikiPage[]) => WikiPage[]
  activeFilterLabel: ComputedRef<string | null>
}

export function useCategoryFilter(pages: Ref<readonly WikiPage[]>): CategoryFilter {
  const activeFilter = ref<string | null>(null)

  const chips = computed<CategoryChip[]>(() => {
    const buckets = new Map<string, number>()
    for (const page of pages.value) {
      const key = chipBucket(page)
      buckets.set(key, (buckets.get(key) ?? 0) + 1)
    }
    return Array.from(buckets.entries())
      .map(([raw, count]) => ({ raw, display: categoryLabel(raw), count }))
      .toSorted((a, b) => {
        if (a.raw === GENERAL_BUCKET) return -1
        if (b.raw === GENERAL_BUCKET) return 1
        return a.display.localeCompare(b.display)
      })
  })

  function toggleFilter(raw: string): void {
    activeFilter.value = activeFilter.value === raw ? null : raw
  }

  function clearFilter(): void {
    activeFilter.value = null
  }

  function filterPages(list: readonly WikiPage[]): WikiPage[] {
    if (activeFilter.value == null) return [...list]
    const target = activeFilter.value
    return list.filter((p) => chipBucket(p) === target)
  }

  const activeFilterLabel = computed(() => (activeFilter.value == null ? null : categoryLabel(activeFilter.value)))

  return { chips, activeFilter, toggleFilter, clearFilter, filterPages, activeFilterLabel }
}
